import { useCallback, useEffect, useState } from "react";
import { ScrollRestoration, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Package } from "lucide-react";

import {
  useGetCartBrandVideosQuery,
  useGetCartCompetitorVideosQuery,
} from "@/redux/feature/brandCart/brandCartApi";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";

import { CustomButton } from "@/components/ui/Buttons";
import Modal from "@/components/ui/Modal";
import ProductDetailsModal from "../ProductCollections/components/productDetailsModal/ProductDetailsModal";

import TopCompetitorIcon from "@/assets/icons/TopCompetitor.svg";
import BrandCartCompetitors from "./components/BrandCartCompetitors";
import ManualCartCompetitorModal from "./components/ManualCartCompetitorModal";
import VideoSection from "../ProductCollections/components/CompetitorVideos/VideoSection";
import Pagination from "@/components/ui/Pagination";

const POLLING_INTERVAL = 10000;

const BrandCartDetails = () => {
  const { brandName } = useParams();
  const navigate = useNavigate();

  // State management
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [isManualCartCompetitorModalOpen, setIsManualCartCompetitorModalOpen] =
    useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<null | string>(null);
  const [pollingStatus, setPollingStatus] = useState({
    brandVideos: true,
    competitorVideos: true,
  });
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);

  const [currentBrandVideosPage, setCurrentBrandVideosPage] = useState(1);
  const [brandVideosLimit] = useState(9);

  const [currentCompetitorVideosPage, setCurrentCompetitorVideosPage] =
    useState(1);
  const [competitorLimit] = useState(9);

  // API calls
  const {
    data: BrandVideosResponse,
    isLoading: isBrandVideosLoading,
    isFetching: isBrandVideosFetching,
  } = useGetCartBrandVideosQuery(
    { brand: brandName, page: currentBrandVideosPage, limit: brandVideosLimit },
    {
      pollingInterval: pollingStatus.brandVideos ? POLLING_INTERVAL : 0,
      skip: !brandName,
    }
  );

  const {
    data: competitorVideosResponse,
    isLoading: isCompetitorLoading,
    isFetching: isCompetitorFetching,
  } = useGetCartCompetitorVideosQuery(
    {
      brand: brandName,
      page: currentCompetitorVideosPage,
      limit: competitorLimit,
    },
    {
      pollingInterval: pollingStatus.competitorVideos ? POLLING_INTERVAL : 0,
      skip: !brandName,
    }
  );

  // Derived values
  // const brandVideos = [
  //   ...(BrandVideosResponse?.data?.brandAds || []),
  //   ...(BrandVideosResponse?.data?.brandReels || []),
  // ];

  const brandVideos = BrandVideosResponse?.data || [];

  const brandVideosLoading =
    brandVideos?.length > 0
      ? false
      : isBrandVideosFetching ||
        isBrandVideosLoading ||
        BrandVideosResponse?.meta?.isProcessing;

  // const competitorVideos = [
  //   ...(competitorVideosResponse?.data?.competitorAds || []),
  //   ...(competitorVideosResponse?.data?.competitorReels || []),
  // ];

  const competitorVideos = competitorVideosResponse?.data || [];

  const competitorVideosLoading =
    brandVideos?.length > 0
      ? false
      : isCompetitorFetching ||
        isCompetitorLoading ||
        competitorVideosResponse?.meta?.isProcessing;

  // Effects
  useEffect(() => {
    setPollingStatus((prev) => ({
      ...prev,
      brandVideos: BrandVideosResponse?.meta?.isProcessing !== false,
      competitorVideos: competitorVideosResponse?.meta?.isProcessing !== false,
    }));
  }, [BrandVideosResponse?.meta, competitorVideosResponse?.meta]);

  // Handlers
  const handleGoBack = useCallback(() => navigate(-1), [navigate]);

  const handleBrandChange = (brand: string | null) => {
    setSelectedBrand(brand);
  };

  useEffect(() => {
    setCurrentBrandVideosPage(1);
    setCurrentCompetitorVideosPage(1);
  }, [brandName]);

  return (
    <div>
      <div className="mb-6 sm:mb-8">
        {/* Header Section */}
        <div className="mb-4 sm:mb-6">
          <CustomButton
            variant="outline"
            fontSize="text-xs sm:text-sm"
            fontWeight="500"
            color="#0D0D0D"
            border="1px solid #E4E4E4"
            borderRadius="30px"
            onClick={handleGoBack}
            className="flex items-center gap-x-2 sm:gap-x-2.5 px-3 py-2 sm:px-4 leading-5 sm:leading-6"
          >
            <ArrowLeft size={18} className="sm:size-6" />
            <span className="hidden sm:inline">Go back to product list</span>
            <span className="sm:hidden">Go back</span>
          </CustomButton>
        </div>

        {/* Product Header */}
        <div className="pb-4 sm:pb-6 border-b border-[#6B6B6B4D] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-x-3">
            <div className="bg-[#00000033] size-12 sm:size-[66px] rounded-full flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h1 className="text-[#0D0D0D] text-xl sm:text-2xl lg:text-3xl font-medium leading-tight capitalize">
              {brandName}
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row lg:gap-8 space-y-6 lg:space-y-0 mt-10">
          {/* Sidebar */}
          <aside className="w-full lg:w-[220px] 2xl:w-[320px] order-1 lg:order-1">
            <div className="lg:sticky lg:top-4">
              <BrandCartCompetitors
                setIsManualCartCompetitorModalOpen={
                  setIsManualCartCompetitorModalOpen
                }
              />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 order-3 lg:order-2">
            <div className="space-y-10">
              <div>
                <VideoSection
                  title="Your brand Videos"
                  icon={<TopCompetitorIcon />}
                  videos={brandVideos || []}
                  isLoading={brandVideosLoading}
                  currentlyPlaying={currentlyPlaying}
                  setCurrentlyPlaying={setCurrentlyPlaying}
                  setIsDetailsModalOpen={setIsDetailsModalOpen}
                  setSelectedVideo={setSelectedVideo}
                />

                {BrandVideosResponse?.meta?.totalPages > 1 && (
                  <div className="mt-16">
                    <Pagination
                      currentPage={currentBrandVideosPage}
                      totalPages={BrandVideosResponse.meta.totalPages}
                      setPage={setCurrentBrandVideosPage}
                    />
                  </div>
                )}
              </div>

              <VideoSection
                title="Your Competitors Videos"
                icon={<TopCompetitorIcon />}
                videos={competitorVideos || []}
                isLoading={competitorVideosLoading}
                currentlyPlaying={currentlyPlaying}
                setCurrentlyPlaying={setCurrentlyPlaying}
                setIsDetailsModalOpen={setIsDetailsModalOpen}
                setSelectedVideo={setSelectedVideo}
              />

              {competitorVideosResponse?.meta?.totalPages > 1 && (
                <div className="mt-16">
                  <Pagination
                    currentPage={currentCompetitorVideosPage}
                    totalPages={competitorVideosResponse.meta.totalPages}
                    setPage={setCurrentCompetitorVideosPage}
                  />
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={isManualCartCompetitorModalOpen}
        setOpen={setIsManualCartCompetitorModalOpen}
        width="600px"
      >
        <ManualCartCompetitorModal
          setIsOpen={setIsManualCartCompetitorModalOpen}
        />
      </Modal>

      <Modal
        padding="12px"
        width="750px"
        isOpen={isDetailsModalOpen}
        setOpen={setIsDetailsModalOpen}
      >
        <ProductDetailsModal
          showGenerateScript={false}
          video={selectedVideo as VideoData}
          setIsDetailsModalOpen={setIsDetailsModalOpen}
          setIsSelectModalOpen={setIsSelectModalOpen}
        />
      </Modal>
      <ScrollRestoration />
    </div>
  );
};

export default BrandCartDetails;
